import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Chip,
  Divider,
  IconButton,
} from '@mui/material';
import {
  ArrowBack,
  Download,
  Star,
  Share,
  Favorite,
  PersonAdd,
} from '@mui/icons-material';
import { Facebook, Twitter, LinkedIn } from '@mui/icons-material';

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) return;

    // Fetch project details
    axios
      .get(`/scripts/getProject.php?ID=${id}`)
      .then((response) => {
        if (response.data.error) {
          setProject(null);
        } else {
          setProject(response.data);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching project:', error);
        setLoading(false);
      });

    // Increase the view count for this project
    axios
      .get(`/scripts/increaseViewCount.php?ID=${id}`)
      .then((response) => {
        if (response.data.error) {
          console.error('Error updating view count:', response.data.error);
        } else {
          console.log('View count updated successfully');
        }
      })
      .catch((error) => {
        console.error('Error updating view count:', error);
      });
  }, [id]);

  const getDifficultyLabel = (difficulty) => {
    const labels = {
      1: 'Easy',
      2: 'Medium',
      3: 'Hard',
      4: 'Extra Hard',
    };
    return labels[difficulty] || null;
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!project) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Project Not Found
        </Typography>
        <Button
          startIcon={<ArrowBack />}
          variant="contained"
          onClick={() => navigate(-1)}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  const shareUrl = window.location.href;

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        variant="text"
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Back to Projects
      </Button>

      {/* Tags Section */}
      <Stack direction="row" spacing={2} mb={3}>
        <Chip
          label={`Published on: ${new Date(
            project.publish_date
          ).toLocaleDateString()}`}
          variant="outlined"
        />
        {project.view_count && (
          <Chip
            label={`Views: ${project.view_count}`}
            variant="outlined"
            color="primary"
          />
        )}
        {project.award_winning === '1' && (
          <Chip
            icon={<Star />}
            label="Award Winning"
            variant="outlined"
            color="success"
          />
        )}
        {getDifficultyLabel(project.difficulty) && (
          <Chip
            label={`Difficulty: ${getDifficultyLabel(project.difficulty)}`}
            variant="outlined"
            color="secondary"
          />
        )}
      </Stack>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Card sx={{ boxShadow: 3 }}>
            <Box
              sx={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}
            >
              <iframe
                src={project.url_video}
                title="YouTube Video"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </Box>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {project.title}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          {(project.url_download || project.url_stl || project.url_model) && (
            <Card sx={{ boxShadow: 3, mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Downloads
                </Typography>
                <Stack direction="column" spacing={2}>
                  {project.url_download && (
                    <Button
                      startIcon={<Download />}
                      variant="contained"
                      href={project.url_download}
                      target="_blank"
                    >
                      Download Project Files
                    </Button>
                  )}
                  {project.url_stl && (
                    <Button
                      startIcon={<Download />}
                      variant="outlined"
                      href={project.url_stl}
                      target="_blank"
                    >
                      Download STL
                    </Button>
                  )}
                  {project.url_model && (
                    <Button
                      startIcon={<Download />}
                      variant="outlined"
                      href={project.url_model}
                      target="_blank"
                    >
                      Download Model
                    </Button>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Share & Interact Section */}
          <Card sx={{ boxShadow: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Share & Interact
              </Typography>
              <Stack direction="row" spacing={2} mb={2}>
                <IconButton
                  component="a"
                  href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                  target="_blank"
                >
                  <Facebook color="primary" />
                </IconButton>
                <IconButton
                  component="a"
                  href={`https://twitter.com/intent/tweet?url=${shareUrl}`}
                  target="_blank"
                >
                  <Twitter color="primary" />
                </IconButton>
                <IconButton
                  component="a"
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
                  target="_blank"
                >
                  <LinkedIn color="primary" />
                </IconButton>
              </Stack>

              <Stack direction="row" spacing={2}>
                {/* <Button startIcon={<Favorite />} variant="outlined">
                  Like
                </Button>
                <Button startIcon={<Share />} variant="outlined">
                  Share
                </Button>
                <Button startIcon={<PersonAdd />} variant="outlined">
                  Follow
                </Button> */}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
        {project.diagram_descr}
      </Typography>
    </Container>
  );
};

export default ProjectDetail;
